import { useConfig } from 'shared/config/ConfigContext';
import useFetch from 'shared/hooks/useFetch';
import { useSharedState } from 'shared/hooks/useSharedState';
import { formatISODate } from 'shared/util/datetime-fns';
import { queryString } from 'shared/util/query';
import { mutate } from 'swr';
import useSWRMutation from 'swr/mutation';
import { postPriceOverrideData } from '@/api/api';
import type { PriceCalendar, PriceCleaningChoice, PriceOverride } from '@/domain';
import { usePriceOverrideInvalidate } from '@/hooks/usePriceOverride';

const url = (
    apiExternalUrl: string,
    adId: string,
    fromDate: string,
    toDate: string,
    guests = 1,
    cleaningChoice?: PriceCleaningChoice | null,
) =>
    `${apiExternalUrl}/calendar/priceprivate${queryString({
        adId: adId,
        fromDate: fromDate,
        toDate: toDate,
        guests: guests,
        cleaningChoice: cleaningChoice,
    })}`;

type DateFromTo = { fromDate: string; toDate: string };
const useSharedDateFromTo = () => useSharedState<DateFromTo>('PRICE_CALENDAR_DATES', { fromDate: '', toDate: '' });

export const usePriceCalendar = (
    adId: string,
    fromDate: Date,
    toDate: Date,
    guests = 1,
    cleaningChoice: PriceCleaningChoice | null = null,
) => {
    const [date, setNewDate] = useSharedDateFromTo();
    const { apiExternalUrl } = useConfig();
    const formattedFromDate = formatISODate(fromDate);
    const formattedToDate = formatISODate(toDate);
    if (!date) {
        throw new Error('Date not being set should not be possible');
    }

    if (date.fromDate !== formattedFromDate || date.toDate !== formattedToDate) {
        setNewDate({ fromDate: formattedFromDate, toDate: formattedToDate });
    }

    const { data: priceCalendar, ...rest } = useFetch<PriceCalendar>({
        url: url(apiExternalUrl, adId, date.fromDate || formattedFromDate, date.toDate || formattedToDate, guests, cleaningChoice),
    });

    return { priceCalendar, ...rest };
};

export const usePriceCalendarMutation = (adId: string) => {
    const { apiExternalUrl } = useConfig();
    const [date] = useSharedDateFromTo();
    const { invalidate } = usePriceOverrideInvalidate(adId);
    const post = async (_url: string, { arg }: { arg: PriceOverride }) => postPriceOverrideData(apiExternalUrl, arg);

    if (!date) {
        throw new Error('Date not being set should not be possible');
    }

    const { trigger, ...rest } = useSWRMutation(url(apiExternalUrl, adId, date.fromDate, date.toDate), post, { throwOnError: false });

    const triggerWithSideEffect = async (arg: PriceOverride) => {
        await trigger(arg);

        if (invalidate) {
            await invalidate();
        }
    };

    return { trigger: triggerWithSideEffect, ...rest };
};

export const usePriceCalendarInvalidate = (adId: string) => {
    const [date] = useSharedDateFromTo();
    const { apiExternalUrl } = useConfig();
    if (!date) return {};

    const invalidate = () => mutate(url(apiExternalUrl, adId, date.fromDate, date.toDate));
    return { invalidate };
};
